import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Edit3, 
  Share2, 
  Eye,
  Plus,
  Settings,
  Users,
  Shield,
  AlertCircle
} from 'lucide-react';

const GestionPDF = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([
    {
      id: 1,
      nom: 'Fiche RDV Template',
      type: 'Template configuré',
      dateCreation: '2024-09-15',
      taille: '2.3 MB',
      autorisations: ['admin', 'commercial', 'secretaire'],
      statut: 'Actif',
      description: 'Template principal pour les fiches de rendez-vous'
    }
  ]);
  
  const [utilisateurs] = useState([
    { id: 'admin', label: 'Administrateurs', icon: Shield },
    { id: 'commercial', label: 'Commerciaux', icon: Users },
    { id: 'secretaire', label: 'Secrétaires', icon: Users },
    { id: 'manager_vente', label: 'Managers Vente', icon: Users },
    { id: 'employe_vente', label: 'Employés Vente', icon: Users }
  ]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [newDocument, setNewDocument] = useState({
    nom: '',
    description: '',
    autorisations: ['admin']
  });

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      const newDoc = {
        id: documents.length + 1,
        nom: file.name.replace('.pdf', ''),
        type: 'Document téléchargé',
        dateCreation: new Date().toISOString().split('T')[0],
        taille: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        autorisations: newDocument.autorisations,
        statut: 'Actif',
        description: newDocument.description
      };
      
      setDocuments([...documents, newDoc]);
      setShowUploadModal(false);
      setNewDocument({ nom: '', description: '', autorisations: ['admin'] });
    } else {
      console.log('Veuillez sélectionner un fichier PDF valide');
    }
  };

  const handlePermissionChange = (role, documentId) => {
    setDocuments(documents.map(doc => {
      if (doc.id === documentId) {
        const newAutorisations = doc.autorisations.includes(role)
          ? doc.autorisations.filter(r => r !== role)
          : [...doc.autorisations, role];
        return { ...doc, autorisations: newAutorisations };
      }
      return doc;
    }));
  };

  const handleDeleteDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const getStatusColor = (statut) => {
    switch(statut) {
      case 'Actif': return 'bg-green-100 text-green-800';
      case 'Inactif': return 'bg-red-100 text-red-800';
      case 'Brouillon': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-indigo-600" />
              Gestion PDF
            </h1>
            <p className="text-gray-600 mt-1">Gérez vos documents PDF et leurs autorisations</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pdf-mapper')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
              Configurer Template RDV
            </button>
            
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter Document
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Documents totaux</p>
                <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Documents actifs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {documents.filter(doc => doc.statut === 'Actif').length}
                </p>
              </div>
              <Eye className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Utilisateurs autorisés</p>
                <p className="text-2xl font-bold text-gray-900">{utilisateurs.length}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Templates configurés</p>
                <p className="text-2xl font-bold text-gray-900">1</p>
              </div>
              <Settings className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Liste des documents */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Documents PDF</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date création
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Taille
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Autorisations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{doc.nom}</div>
                          <div className="text-sm text-gray-500">{doc.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{doc.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{doc.dateCreation}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{doc.taille}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(doc.statut)}`}>
                        {doc.statut}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {doc.autorisations.map((role) => {
                          const user = utilisateurs.find(u => u.id === role);
                          return (
                            <span key={role} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              {user?.label || role}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedDocument(doc);
                            setShowPermissionsModal(true);
                          }}
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="Gérer les autorisations"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        
                        <button
                          className="p-1 text-green-600 hover:text-green-800"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal d'ajout de document */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4">Ajouter un nouveau document PDF</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du document
                  </label>
                  <input
                    type="text"
                    value={newDocument.nom}
                    onChange={(e) => setNewDocument({...newDocument, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Nom du document"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newDocument.description}
                    onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows="3"
                    placeholder="Description du document"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Autorisations par défaut
                  </label>
                  <div className="space-y-2">
                    {utilisateurs.map((user) => (
                      <label key={user.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newDocument.autorisations.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewDocument({
                                ...newDocument,
                                autorisations: [...newDocument.autorisations, user.id]
                              });
                            } else {
                              setNewDocument({
                                ...newDocument,
                                autorisations: newDocument.autorisations.filter(r => r !== user.id)
                              });
                            }
                          }}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">{user.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier PDF
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de gestion des autorisations */}
        {showPermissionsModal && selectedDocument && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4">
                Autorisations - {selectedDocument.nom}
              </h3>
              
              <div className="space-y-3">
                {utilisateurs.map((user) => (
                  <label key={user.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center">
                      <user.icon className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-sm font-medium text-gray-700">{user.label}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedDocument.autorisations.includes(user.id)}
                      onChange={() => handlePermissionChange(user.id, selectedDocument.id)}
                      className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                  </label>
                ))}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionPDF;